import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './InsightCards.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Lightweight inline-markdown renderer for insight card text.
// Insight bodies/titles are short prose (no block elements), so we only handle
// inline marks: **bold**, *italic*/_italic_, `code`. Everything is HTML-escaped
// first so model output can never inject markup.
function renderInlineMarkdown(text) {
  if (!text) return '';
  let html = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '<em>$1</em>');
  html = html.replace(/(?<!\w)_([^_]+?)_(?!\w)/g, '<em>$1</em>');
  return html;
}

// Friendly source labels per tab
const SOURCE_LABELS = {
  network: 'Network Graph',
  heatmap: 'Heat Map',
  umap: 'Topic Clusters',
  summary: 'Summary',
};

/**
 * InsightCards — floating card carousel with prev/next arrows.
 * Renders as a hovering card over the dashboard visualization area.
 * Single-tab mode: one card deck. No merged mode — Landscape uses two separate instances.
 */
export default function InsightCards({ 
  tab,
  filters,
  topicView,
  selectedNodes, 
  authFetch, 
  onAskAbout,
  onOpenDocument,
  onOpenRepresentativeDoc,
}) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emptyReason, setEmptyReason] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [persona, setPersona] = useState('');
  const floatRef = useRef(null);
  const dragState = useRef(null);

  // Drag handling — drag by the header bar
  const onDragStart = useCallback((e) => {
    // Only start drag from the drag handle area
    if (e.target.closest('button')) return;
    e.preventDefault();
    const rect = floatRef.current?.getBoundingClientRect();
    const parentRect = floatRef.current?.offsetParent?.getBoundingClientRect();
    if (!rect || !parentRect) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origLeft: rect.left - parentRect.left,
      origTop: rect.top - parentRect.top,
    };
    const onMove = (ev) => {
      if (!dragState.current) return;
      const dx = ev.clientX - dragState.current.startX;
      const dy = ev.clientY - dragState.current.startY;
      if (floatRef.current) {
        floatRef.current.style.right = 'auto';
        floatRef.current.style.left = `${dragState.current.origLeft + dx}px`;
        floatRef.current.style.top = `${dragState.current.origTop + dy}px`;
      }
    };
    const onUp = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Stabilize selectedNodes — only re-trigger when the actual IDs change, not Set reference
  const stableNodeKey = useMemo(() => {
    if (!selectedNodes || selectedNodes.size === 0) return '';
    return Array.from(selectedNodes).sort().join(',');
  }, [selectedNodes]);

  // Keep refs to latest values so the debounced fetch always reads current state
  const tabRef = useRef(tab);
  const filtersRef = useRef(filters);
  const topicViewRef = useRef(topicView);
  const stableNodeKeyRef = useRef(stableNodeKey);
  tabRef.current = tab;
  filtersRef.current = filters;
  topicViewRef.current = topicView;
  stableNodeKeyRef.current = stableNodeKey;

  // Version counter — each fetch gets a version; stale responses are silently discarded.
  // This replaces AbortController-based cancellation which was aborting legitimate
  // first-render fetches when React re-ran effects or dependencies stabilized.
  const fetchVersionRef = useRef(0);
  const retryCountRef = useRef(0);

  const fetchInsights = useCallback(async (isAutoRetry = false) => {
    const version = ++fetchVersionRef.current;

    setLoading(true);
    setError(null);
    setEmptyReason(null);
    setDismissed(false);

    try {
      // Read current values from refs to avoid stale closures
      const currentTab = tabRef.current;
      const currentFilters = filtersRef.current;
      const currentTopicView = topicViewRef.current;
      const currentNodeKey = stableNodeKeyRef.current;
      const nodeArray = currentNodeKey ? currentNodeKey.split(',') : [];

      const response = await authFetch(`${API_BASE_URL}/api/insight/insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tab: currentTab,
          filters: currentFilters,
          view: currentTopicView || undefined,
          selected_nodes: nodeArray,
        }),
      });

      // Stale response — a newer fetch was triggered while this one was in flight
      if (version !== fetchVersionRef.current) return;

      if (!response.ok) {
        if (response.status === 503) { setInsights([]); return; }
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.reason || `Insight generation failed (${response.status})`);
      }

      const data = await response.json();
      const tagged = (data.insights || []).map(insight => ({ ...insight, source: currentTab }));

      // Auto-retry once if we got an empty result (transient LLM failure)
      if (tagged.length === 0 && !data.reason && !isAutoRetry) {
        retryCountRef.current += 1;
        setTimeout(() => fetchInsights(true), 1500);
        return; // stay in loading state
      }

      setInsights(tagged);
      setActiveIndex(0);
      retryCountRef.current = 0;
      if (data.persona) setPersona(data.persona);

      // Show empty reason if backend signalled insufficient data
      if (tagged.length === 0 && data.reason) {
        setEmptyReason(data.reason);
      } else {
        setEmptyReason(null);
      }
    } catch (err) {
      if (version !== fetchVersionRef.current) return;
      setError(err.message);
      setInsights([]);
    } finally {
      if (version === fetchVersionRef.current) {
        setLoading(false);
      }
    }
  }, [authFetch]);

  // Re-trigger insight generation when tab, filters, or selected nodes change.
  // The 800ms debounce collapses rapid sequential state updates (e.g. node click
  // followed by neighbour highlighting) into a single API call.
  useEffect(() => {
    const timeoutId = setTimeout(fetchInsights, 800);
    return () => clearTimeout(timeoutId);
  }, [tab, stableNodeKey, filters, topicView, fetchInsights]);

  // Hide when empty / dismissed
  if (dismissed) return null;
  if (!loading && insights.length === 0 && !error && !emptyReason) return null;

  const current = insights[activeIndex];
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < insights.length - 1;

  return (
    <div className="insight-float" ref={floatRef}>
      {minimized ? (
        <div className="insight-float-minimized" onMouseDown={onDragStart}>
          <span className="insight-minimized-label">{persona ? `${persona} | insight` : 'insight'}</span>
          {insights.length > 0 && <span className="insight-minimized-count">{insights.length}</span>}
          <button className="insight-minimize-btn" onClick={() => setMinimized(false)} title="Expand">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><polyline points="2,7 5,3 8,7"/></svg>
          </button>
        </div>
      ) : loading ? (
        <div className="insight-float-card insight-float-loading" onMouseDown={onDragStart}>
          <div className="insight-loading-dots"><span /><span /><span /></div>
          <span className="insight-loading-text">Decoding patterns…</span>
        </div>
      ) : error ? (
        <div className="insight-float-card insight-float-error" onMouseDown={onDragStart}>
          <div className="insight-error-content">
            <span className="insight-error-msg">{error}</span>
            <div className="insight-error-actions">
              <button className="insight-retry-btn" onClick={fetchInsights}>Retry</button>
              <button className="insight-dismiss" onClick={() => setDismissed(true)}>&times;</button>
            </div>
          </div>
        </div>
      ) : emptyReason ? (
        <div className="insight-float-card insight-float-empty" onMouseDown={onDragStart}>
          <div className="insight-error-content">
            <span className="insight-error-msg">{emptyReason}</span>
            <div className="insight-error-actions">
              <button className="insight-retry-btn" onClick={fetchInsights}>Retry — this often resolves on a second attempt</button>
              <button className="insight-dismiss" onClick={() => setDismissed(true)}>&times;</button>
            </div>
          </div>
        </div>
      ) : current ? (
        <div className="insight-float-card">
          <div className="insight-drag-bar" onMouseDown={onDragStart}>
            <span className="insight-drag-label">{persona ? `${persona} | insight` : 'insight'}</span>
            <div className="insight-drag-actions">
              <button className="insight-minimize-btn" onClick={() => setMinimized(true)} title="Minimize">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="2" y1="5" x2="8" y2="5"/></svg>
              </button>
              <button className="insight-dismiss" onClick={() => setDismissed(true)} title="Dismiss">&times;</button>
            </div>
          </div>
          <div className="insight-card-header">
            <span className={`insight-card-category ${current.category || 'pattern'}`}>
              {current.category || 'pattern'}
            </span>
            <span className="insight-card-source">
              {SOURCE_LABELS[current.source] || current.source}
            </span>
          </div>
          <div className="insight-card-title" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(current.title) }} />
          <div className="insight-card-body" dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(current.body) }} />
          {Array.isArray(current.evidence) && current.evidence.length > 0 && (
            <div className="insight-evidence">
              <div className="insight-evidence-label">From the data</div>
              <div className="insight-evidence-chips">
                {current.evidence.map((ev, i) => (
                  <span key={i} className="insight-evidence-chip" title="A figure from the current view supporting this insight">{ev}</span>
                ))}
              </div>
            </div>
          )}
          {onAskAbout && Array.isArray(current.follow_ups) && current.follow_ups.length > 0 && (
            <div className="insight-followups">
              <div className="insight-followups-label">Explore further</div>
              {current.follow_ups.map((fu, i) => (
                <button
                  key={i}
                  className={`insight-followup-chip ${fu.mode === 'documents' ? 'mode-docs' : 'mode-data'}`}
                  onClick={() => onAskAbout(current.title, current.body, fu.q)}
                  title={fu.mode === 'documents' ? 'Searches the document corpus' : 'Answered from dashboard data'}
                >
                  <span className="insight-followup-icon">{fu.mode === 'documents' ? '📄' : '📊'}</span>
                  {fu.q}
                </button>
              ))}
            </div>
          )}
          <div className="insight-card-footer">
            {onAskAbout && (
              <button
                className="insight-ask-btn"
                onClick={() => onAskAbout(current.title, current.body)}
              >
                Ask about this &#x2192;
              </button>
            )}
            {/* Open source document: shown only when the insight payload
                carries a doc reference. The insight generator opts in by
                emitting `filename` (+ optional `paraIdx`) or `topic`/`industry`
                fields on the insight object. */}
            {onOpenDocument && current.filename && (
              <button
                className="insight-ask-btn"
                onClick={() => onOpenDocument(current.filename, current.paraIdx ?? null)}
                title="Open the source document for this insight"
              >
                Open source &#x2197;
              </button>
            )}
            {onOpenRepresentativeDoc && !current.filename && (current.topic || current.industry) && (
              <button
                className="insight-ask-btn"
                onClick={() => onOpenRepresentativeDoc({ topic: current.topic, industry: current.industry })}
                title="Open a representative source document"
              >
                Open source &#x2197;
              </button>
            )}
            {insights.length > 1 && (
              <div className="insight-nav">
                <button
                  className="insight-nav-btn"
                  disabled={!hasPrev}
                  onClick={() => setActiveIndex(i => i - 1)}
                  aria-label="Previous insight"
                >&#x2039;</button>
                <span className="insight-nav-count">{activeIndex + 1}/{insights.length}</span>
                <button
                  className="insight-nav-btn"
                  disabled={!hasNext}
                  onClick={() => setActiveIndex(i => i + 1)}
                  aria-label="Next insight"
                >&#x203A;</button>
              </div>
            )}
          </div>
          <div className="insight-disclaimer">AI-generated &mdash; may contain errors. Please verify.</div>
        </div>
      ) : null}
    </div>
  );
}
